<script lang="ts" setup>
import UserAvatar from '@/components/UserAvatar.vue'
import ContactListItemTag from '@/components/Contact/ContactListItemTag.vue'
import {computed, withDefaults} from 'vue'
import {Contact} from "@/models/dto/ContactGroup";
import { useDisplay } from '@/composables/useDisplay'
const { xs } = useDisplay()
const fullscreen = computed(() => xs.value)


const emit = defineEmits<{
  (e: 'edit'): void
  (e: 'delete:contact'): void
}>()

const props = withDefaults(defineProps<{
  id: string
  isBookingContact?: boolean
  editable?: boolean
  contact: Contact
  /** Controls tag position relative to user info. * true: tag appears next to name * false: tag appears on the right side */
  alignLeft?: boolean
  hideDelete?: boolean
}>(),
 {
  editable: false,
  alignLeft: false,
  hideDelete: false,
})

const tagText = computed<string>(() => {
  if (props.isBookingContact) {
    return 'Default Trip Contact'
  }
  if (props.contact.isTripContact) {
    return 'Trip Contact'
  }
  return ''
})

const tagTooltipText = computed(() => {
  if (props.isBookingContact) {
    return 'For any trip day communication, we\'ll contact this person first before contacting other Trip Contacts.'
  }
  if (props.contact.isTripContact) {
    return 'If we can\'t reach the booking contact on trip day, we\'ll contact this person as a backup. Best to choose someone on the bus.'
  }
  return ''
})

</script>

<template>
  <div :id="id" class="d-flex flex-row align-center padding-y-2">
    <UserAvatar
      :id="`${id}-user-avatar`"
      class="margin-r-4"
      :image-path="contact.profileImagePath"
      :initials="contact.initials"
      :current-user="contact.isCurrentUser"
    />

    <div class="d-flex flex-column">
      <div v-if="alignLeft" class="d-flex flex-row align-center flex-wrap" style="gap: 8px">
        <p v-if="contact.name" id="contact-list-item-name" class="font-14 margin-t-0">{{ contact.name }}</p>
        <ContactListItemTag
          v-if="tagText"
          :tag-text="tagText"
        />
        <p
          v-if="contact.canModifyTrip"
          id="contact-list-item-can-modify-trip"
          class="font-12 margin-t-0 text-gray-light" style="white-space: nowrap;"
        >
          (Can Modify/Cancel)
        </p>
      </div>
      <p id="contact-list-item-email" class="font-12 margin-t-0">
        {{ contact.email }}
        <span v-if="contact.phone"> · {{ contact.phone }}</span>
      </p>
      <p
          v-if="!alignLeft && contact.canModifyTrip"
          id="contact-list-item-can-modify-trip"
          class="font-12 margin-t-0 text-gray-light" style="white-space: nowrap;"
        >
          (Can Modify/Cancel)
        </p>
    </div>

    <v-spacer />
    <ContactListItemTag
      v-if="!alignLeft && tagText"
      :tag-text="tagText"
      :tag-tooltip-text="tagTooltipText"
    />

    <div v-if="editable" class="flex-shrink-0">
      <CUIcon
        id="contact-list-item-edit-icon"
        view-box="0 0 24 24"
        width="20"
        height="20"
        color="gray-medium-light"
        hover-color="primary"
        @click="emit('edit')"
      >
        edit
      </CUIcon>
      <CUIcon
        v-if="!fullscreen && !hideDelete"
        id="contact-list-item-delete-icon"
        view-box="0 0 24 24"
        width="20"
        height="20"
        color="gray-medium-light"
        hover-color="error"
        class="margin-l-3"
        @click="emit('delete:contact')"
      >
        delete
      </CUIcon>
    </div>
  </div>
</template>
